import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Panel } from "@/components/Panel";
import { toast } from "sonner";
import { Slack as SlackIcon, CheckCircle2, ExternalLink, Loader2, Power } from "lucide-react";

export default function IntegrationsTab({ isOwner }) {
  const [sp, setSp] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [working, setWorking] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/slack/install/status");
      setStatus(r.data);
    } catch (e) {
      setStatus({ connected: false, configured: false });
    }
  };
  useEffect(() => { load(); }, []);

  // Toast on OAuth-callback redirect
  useEffect(() => {
    if (sp.get("slack") === "connected") {
      toast.success("Slack connected. Mention @DARA in any channel.");
      sp.delete("slack");
      setSp(sp, { replace: true });
      load();
    }
    const err = sp.get("slack_error");
    if (err) {
      toast.error(`Slack install failed: ${err}`);
      sp.delete("slack_error");
      setSp(sp, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectSlack = async () => {
    setWorking(true);
    try {
      const r = await api.post("/slack/install/start");
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start Slack install");
      setWorking(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect this workspace from Slack? @DARA mentions will stop working.")) return;
    setWorking(true);
    try {
      await api.delete("/slack/install");
      toast.success("Slack disconnected.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed");
    }
    setWorking(false);
  };

  if (!status) return <div className="p-10 font-mono text-[11px] text-zinc-500">LOADING…</div>;

  return (
    <div className="space-y-6" data-testid="integrations-tab">
      <Panel title="// SLACK · DARA BOT" meta={<SlackIcon className="w-3.5 h-3.5" />}>
        <div className="p-6 max-w-2xl">
          <div className="flex items-start gap-4 mb-5">
            <div className="w-12 h-12 border border-[#FFB000]/40 bg-[#FFB000]/10 flex items-center justify-center flex-shrink-0">
              <SlackIcon className="w-6 h-6 text-[#FFB000]" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading font-bold text-xl mb-1">Ask DARA from any Slack channel.</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">
                Install the DARA bot into your team&apos;s Slack workspace, then mention <code className="text-[#FFB000] bg-zinc-900 px-1.5 py-0.5">@DARA</code> followed by any business question.
                DARA grounds the answer in this workspace&apos;s data and replies in-thread within seconds.
              </p>
            </div>
          </div>

          {!status.configured && (
            <div className="border border-[#FFB000]/30 bg-[#FFB000]/5 p-4 mb-4 font-mono text-[11px] text-[#FFB000] leading-relaxed" data-testid="slack-not-configured">
              ⚠ This DARA instance hasn&apos;t been configured for Slack yet. Operator must set
              <code className="mx-1 text-white">SLACK_CLIENT_ID</code>,
              <code className="mx-1 text-white">SLACK_CLIENT_SECRET</code>, and
              <code className="mx-1 text-white">SLACK_SIGNING_SECRET</code> in the backend environment.
            </div>
          )}

          <div className="flex items-center justify-between p-4 border border-zinc-800">
            <div className="min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">STATUS</div>
              {status.connected ? (
                <div className="mt-1 flex items-center gap-2 text-[#00E676]" data-testid="slack-connected-status">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="font-heading font-bold text-sm">Connected to {status.install?.team_name || "Slack"}</span>
                </div>
              ) : (
                <div className="mt-1 font-mono text-sm text-zinc-500" data-testid="slack-disconnected-status">○ Not connected</div>
              )}
              {status.connected && status.install?.installed_at && (
                <div className="mt-1 font-mono text-[10px] text-zinc-600">
                  Installed {new Date(status.install.installed_at).toLocaleDateString()}
                </div>
              )}
            </div>

            {isOwner && (
              <div className="flex gap-2">
                {status.connected ? (
                  <button
                    onClick={disconnect}
                    disabled={working}
                    data-testid="slack-disconnect-btn"
                    className="border border-zinc-700 px-4 py-2.5 font-mono text-[11px] uppercase tracking-widest hover:border-[#FF3B30]/50 hover:text-[#FF3B30] flex items-center gap-2 disabled:opacity-50"
                  >
                    <Power className="w-3.5 h-3.5" /> Disconnect
                  </button>
                ) : (
                  <button
                    onClick={connectSlack}
                    disabled={!status.configured || working}
                    data-testid="slack-connect-btn"
                    className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-2.5 hover:bg-[#E59E00] flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {working ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5" />}
                    Add to Slack
                  </button>
                )}
              </div>
            )}
          </div>

          {status.connected && (
            <div className="mt-4 font-mono text-[11px] text-zinc-400 leading-relaxed border-l-2 border-zinc-800 pl-3">
              <div className="text-zinc-500 uppercase tracking-widest text-[9px] mb-1">TRY IT</div>
              In any channel where you&apos;ve invited <code className="text-[#FFB000]">@DARA</code>, type:<br />
              <code className="text-zinc-200">@DARA what&apos;s our biggest revenue opportunity right now?</code>
            </div>
          )}
        </div>
      </Panel>
    </div>
  );
}
